import { useState } from "react";
import { X, Loader2, Volume2, VolumeX, ShieldOff, Keyboard } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const COLORS = ["#FF3B30", "#00B0FF", "#00E676", "#FFD600", "#AB47BC", "#FF6D00", "#26C6DA", "#EC407A"];
const SHORTCUTS = [
  ["g + p", "Ir al Panel"], ["g + m", "Ir a Mis tareas"], ["g + t", "Ir a Tableros"],
  ["g + a", "Ir a Actividad"], ["g + i", "Ir a Informes"], ["?", "Ver atajos"],
];

export const ProfileDialog = ({ user, onClose }) => {
  const [name, setName] = useState(user?.name || "");
  const [color, setColor] = useState(user?.avatar_color || COLORS[0]);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [sound, setSound] = useState(localStorage.getItem("notifSound") !== "off");

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { name, avatar_color: color, password: password || null });
      toast.success("Perfil actualizado");
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSaving(false);
    }
  };

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    localStorage.setItem("notifSound", next ? "on" : "off");
  };

  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
      toast.success("Sesiones cerradas en el resto de dispositivos");
    } catch { toast.error("No se pudo cerrar sesiones"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="profile-dialog">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold">Mi perfil y preferencias</h2>
          <button onClick={onClose} data-testid="close-profile"><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nombre</label>
            <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Color de avatar</label>
            <div className="flex gap-2 mt-2">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                  className={`h-7 w-7 rounded-full transition-transform ${color === c ? "ring-2 ring-white scale-110" : "hover:scale-110"}`} style={{ background: c }} />
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nueva contraseña (opcional)</label>
            <input data-testid="profile-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres"
              className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <button onClick={save} disabled={saving} data-testid="save-profile-button"
            className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar cambios
          </button>

          <div className="border-t border-white/10 pt-4 space-y-2">
            <button onClick={toggleSound} data-testid="toggle-sound" className="w-full flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2.5 text-sm hover:bg-white/5">
              {sound ? <Volume2 className="h-4 w-4 text-[#00E676]" /> : <VolumeX className="h-4 w-4 text-zinc-500" />}
              Sonido de notificaciones: <b>{sound ? "activado" : "desactivado"}</b>
            </button>
            <button onClick={logoutAll} data-testid="logout-all-button" className="w-full flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2.5 text-sm hover:bg-red-500/10 hover:text-red-400">
              <ShieldOff className="h-4 w-4" /> Cerrar sesión en todos los dispositivos
            </button>
          </div>

          <div className="border-t border-white/10 pt-4">
            <p className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5 mb-2"><Keyboard className="h-3 w-3" /> Atajos de teclado</p>
            <div className="grid grid-cols-2 gap-1.5">
              {SHORTCUTS.map(([k, d]) => (
                <div key={k} className="flex items-center gap-2 text-xs text-zinc-400">
                  <kbd className="bg-white/5 border border-white/10 rounded px-1.5 py-0.5 font-mono">{k}</kbd> {d}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
